import { useState, useEffect, useMemo, useRef } from 'react'
import { Layers, ChevronDown, ChevronUp, Plus, Trash2, RefreshCw, Zap } from 'lucide-react'
import clsx from 'clsx'
import { useStore } from '../../store/useStore'
import { fetchOHLCV } from '../../api/client'

type AssetType = 'jpy' | 'usd'

function getAssetType(symbol: string): AssetType {
  const s = symbol.toUpperCase()
  if (s.endsWith('.T') || s.replace('=X', '').includes('JPY')) return 'jpy'
  return 'usd'
}

function isCrypto(symbol: string): boolean {
  const s = symbol.toUpperCase()
  return s.endsWith('USDT') || s.endsWith('BTC') || s.endsWith('ETH')
}

// 暗号通貨はBTC等の高額資産だとリスク許容額から逆算した数量が1単位未満に
// なるのが通常のため、整数単位への切り捨てだと常に0になってしまう。
// 暗号通貨のみ小数点以下6桁まで許容する。
function qtyDecimalsFor(symbol: string): number {
  return isCrypto(symbol) ? 6 : 0
}

function fmt(n: number, dec = 0) {
  return n.toLocaleString('ja-JP', { minimumFractionDigits: dec, maximumFractionDigits: dec })
}

interface SymbolRow {
  id: string
  symbol: string
  entryPrice: string
  takeProfit: string
  stopLoss: string
  accountRate: string
  spread: string
}

interface OrderLevels {
  dir: 'BUY' | 'SELL'
  rateDiff: number
  shift: number
  entry: number
  takeProfit: number | null
  stopLoss: number | null
}

// 口座の実勢レート・スプレッドから、実際に発注する利確/損切りレートを算出する。
// エントリーは口座の現在レートをそのまま使う(それが実際に約定するレートその
// ものなので、追加のシフトは加えない)。TP/SLは、システム側(Chronosのデータ
// ソース)のレートと口座の実勢レートのズレ + スプレッド(BUYはask側=上振れ、
// SELLはbid側=下振れ)を、システム側の値からの同一シフト量として反映する。
function computeOrderLevels(
  entry: number, tp: number, sl: number, acct: number, spread: number, actionHint: 'BUY' | 'SELL' | 'HOLD' | undefined,
): OrderLevels | null {
  if (!entry || entry <= 0 || !acct || acct <= 0) return null
  const dir: 'BUY' | 'SELL' | null =
    actionHint === 'BUY' || actionHint === 'SELL' ? actionHint
    : sl > 0 && sl < entry ? 'BUY'
    : sl > 0 && sl > entry ? 'SELL'
    : null
  if (!dir) return null

  const rateDiff = acct - entry
  const shift     = rateDiff + (dir === 'BUY' ? spread / 2 : -spread / 2)
  return {
    dir, rateDiff, shift,
    entry:      acct,
    takeProfit: tp > 0 ? tp + shift : null,
    stopLoss:   sl > 0 ? sl + shift : null,
  }
}

interface RowResult {
  qty: number
  riskJpy: number
  marginJpy: number
  slPct: number
}

function calcRow(
  entry: number, sl: number, balance: number, riskPct: number, fxRate: number, isJpy: boolean, lev: number,
  qtyDecimals: number,
): RowResult | null {
  if (!entry || !sl || !balance || !riskPct || entry <= 0 || sl <= 0 || balance <= 0 || riskPct <= 0) return null
  const slDist = Math.abs(entry - sl)
  if (slDist === 0) return null
  const riskBudgetJpy = balance * (riskPct / 100)
  const qtyRaw = isJpy ? riskBudgetJpy / slDist : (riskBudgetJpy / fxRate) / slDist
  const qtyScale = Math.pow(10, qtyDecimals)
  const qty = Math.floor(qtyRaw * qtyScale) / qtyScale
  if (qty <= 0) return null
  const riskJpy    = isJpy ? qty * slDist : qty * slDist * fxRate
  const notionalJpy = isJpy ? qty * entry : qty * entry * fxRate
  const marginJpy  = notionalJpy / lev
  const slPct      = (slDist / entry) * 100
  return { qty, riskJpy, marginJpy, slPct }
}

let _nextId = 1
function newRow(symbol = '', entryPrice = '', stopLoss = '', takeProfit = ''): SymbolRow {
  return { id: String(_nextId++), symbol, entryPrice, takeProfit, stopLoss, accountRate: '', spread: '' }
}

export default function PortfolioSizingPanel() {
  // activeSignal: 実際の売買シグナル(ADX/RSI/MACDルールフィルタ通過済み)。
  // AI軍師の自己申告的なtrade_recommendationとは別物で、実データでバックテスト
  // 検証済みの本番シグナルそのもの。
  const { alerts, activeSignal, predictions, selectedInterval } = useStore()

  const [open, setOpen]             = useState(false)
  const [balance, setBalance]       = useState('')
  const [riskPct, setRiskPct]       = useState('2')
  const [fxRate, setFxRate]         = useState('150')
  const [fxRateLoading, setFxRateLoading] = useState(false)
  const [leverage, setLeverage]     = useState('')
  const [rows, setRows]             = useState<SymbolRow[]>([])
  const fxFetchedRef = useRef(false)

  // 計算機を開いた際に USD/JPY レートを自動取得（初回のみ）
  useEffect(() => {
    if (!open || fxFetchedRef.current) return
    fxFetchedRef.current = true
    setFxRateLoading(true)
    fetchOHLCV('USDJPY=X', '1d', 1)
      .then(res => {
        const close = res.data[res.data.length - 1]?.close
        if (close && close > 0) setFxRate(String(Math.round(close * 100) / 100))
      })
      .catch(() => { /* デフォルト 150 を維持 */ })
      .finally(() => setFxRateLoading(false))
  }, [open])

  // 直近48時間のアラート対象シンボル（重複排除、通貨コード除外）
  const alertedSymbols = useMemo(() => {
    const cutoff = Date.now() - 48 * 3600 * 1000
    const seen   = new Set<string>()
    return alerts
      .filter(a => {
        const ts = new Date(a.timestamp).getTime()
        return ts > cutoff && a.symbol && a.symbol.length > 3
      })
      .map(a => a.symbol)
      .filter(sym => !seen.has(sym) && seen.add(sym))
      .slice(0, 8)
  }, [alerts])

  function populateFromAlerts() {
    setRows(alertedSymbols.map(sym => {
      const sig   = activeSignal[sym]
      const pred  = predictions[`${sym}_${selectedInterval}`]
      const entry = sig?.entry_price ?? pred?.current_price ?? null
      const tp    = sig?.take_profit ?? null
      const sl    = sig?.stop_loss ?? null
      return newRow(
        sym,
        entry != null ? String(entry) : '',
        sl    != null ? String(sl)    : '',
        tp    != null ? String(tp)    : '',
      )
    }))
  }

  function fillRowFromSignal(idx: number) {
    const sym  = rows[idx].symbol
    const sig  = activeSignal[sym]
    const pred = predictions[`${sym}_${selectedInterval}`]
    if (!sig && !pred) return
    const entry = sig?.entry_price ?? pred?.current_price ?? null
    const tp    = sig?.take_profit ?? null
    const sl    = sig?.stop_loss ?? null
    setRows(rows.map((r, i) => i !== idx ? r : {
      ...r,
      entryPrice: entry != null ? String(entry) : r.entryPrice,
      takeProfit: tp    != null ? String(tp)    : r.takeProfit,
      stopLoss:   sl    != null ? String(sl)    : r.stopLoss,
    }))
  }

  function updateRow(idx: number, patch: Partial<SymbolRow>) {
    setRows(rows.map((r, i) => i === idx ? { ...r, ...patch } : r))
  }

  function removeRow(idx: number) {
    setRows(rows.filter((_, i) => i !== idx))
  }

  const bal  = parseFloat(balance)
  const rPct = parseFloat(riskPct)
  const fx   = parseFloat(fxRate) || 150
  const lev  = parseFloat(leverage) || 1

  const results: (RowResult | null)[] = rows.map(row => {
    const entry = parseFloat(row.entryPrice)
    const sl    = parseFloat(row.stopLoss)
    const isJpy = getAssetType(row.symbol) === 'jpy'
    return calcRow(entry, sl, bal, rPct, fx, isJpy, lev, qtyDecimalsFor(row.symbol))
  })

  const totalRiskJpy   = results.reduce((s, r) => s + (r?.riskJpy   ?? 0), 0)
  const totalMarginJpy = results.reduce((s, r) => s + (r?.marginJpy ?? 0), 0)
  const totalRiskPct   = bal > 0 ? (totalRiskJpy   / bal) * 100 : 0
  const totalMarginPct = bal > 0 ? (totalMarginJpy / bal) * 100 : 0
  const validCount     = results.filter(Boolean).length

  return (
    <div className="rounded-lg border border-trading-border bg-trading-panel overflow-hidden">
      {/* ヘッダー */}
      <button
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-trading-border/20 transition-colors"
        onClick={() => setOpen(v => !v)}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <Layers className="w-4 h-4 text-trading-blue flex-none" />
          <span className="text-sm font-semibold text-trading-text">分散ポジション計算機</span>
          {validCount > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-trading-blue/20 text-trading-blue border border-trading-blue/30 font-bold">
              {validCount} ペア・合計リスク {totalRiskPct.toFixed(1)}%
            </span>
          )}
        </div>
        {open
          ? <ChevronUp   className="w-4 h-4 text-trading-muted flex-none" />
          : <ChevronDown className="w-4 h-4 text-trading-muted flex-none" />}
      </button>

      {open && (
        <div className="border-t border-trading-border px-4 py-4 space-y-4">

          {/* 共通設定 */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: '残高', value: balance, set: setBalance, placeholder: '1000000', unit: '円' },
              { label: 'リスク/ペア', value: riskPct, set: setRiskPct, placeholder: '2', unit: '%' },
              { label: 'レバレッジ', value: leverage, set: setLeverage, placeholder: '空欄=1倍', unit: '倍' },
              { label: fxRateLoading ? 'USD/JPY (取得中…)' : 'USD/JPY', value: fxRate, set: setFxRate, placeholder: '150', unit: '円' },
            ].map(({ label, value, set, placeholder, unit }) => (
              <div key={label} className="flex flex-col gap-0.5">
                <label className="text-[10px] text-trading-muted uppercase tracking-wider">{label}</label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number" min="0" step="any"
                    className="input-field flex-1 text-sm"
                    placeholder={placeholder}
                    value={value}
                    onChange={e => set(e.target.value)}
                  />
                  <span className="text-[11px] text-trading-muted flex-none">{unit}</span>
                </div>
              </div>
            ))}
          </div>

          {/* 操作ボタン */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={populateFromAlerts}
              disabled={alertedSymbols.length === 0}
              className={clsx(
                'flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded border transition-opacity',
                alertedSymbols.length > 0
                  ? 'text-trading-blue border-trading-blue/40 hover:opacity-70'
                  : 'text-trading-muted border-trading-border cursor-not-allowed opacity-40',
              )}
            >
              <Zap className="w-3 h-3" />
              アラート中のペアを自動取得（{alertedSymbols.length}）
            </button>
            <button
              onClick={() => setRows(r => [...r, newRow()])}
              className="flex items-center gap-1.5 text-[11px] text-trading-muted border border-trading-border px-2.5 py-1 rounded hover:opacity-70 transition-opacity"
            >
              <Plus className="w-3 h-3" />
              行を追加
            </button>
          </div>

          {/* ペア行 */}
          {rows.length > 0 ? (
            <div className="space-y-2">
              {rows.map((row, idx) => {
                const res       = results[idx]
                const isJpy     = getAssetType(row.symbol) === 'jpy'
                const hasSignal = !!activeSignal[row.symbol]?.recommend_entry
                  && activeSignal[row.symbol]?.entry_price != null

                const orderLevels = computeOrderLevels(
                  parseFloat(row.entryPrice), parseFloat(row.takeProfit), parseFloat(row.stopLoss),
                  parseFloat(row.accountRate), parseFloat(row.spread) || 0,
                  activeSignal[row.symbol]?.action,
                )

                return (
                  <div key={row.id} className="rounded-md bg-trading-bg border border-trading-border/60 px-3 py-2.5 space-y-2">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
                      {/* 銘柄 */}
                      <div className="flex flex-col gap-0.5">
                        <label className="text-[10px] text-trading-muted uppercase tracking-wider">銘柄</label>
                        <div className="flex items-center gap-1">
                          <input
                            className="input-field text-xs flex-1 font-mono uppercase"
                            placeholder="USDJPY=X"
                            value={row.symbol}
                            onChange={e => updateRow(idx, { symbol: e.target.value.toUpperCase() })}
                          />
                          <span className={clsx(
                            'text-[9px] px-1 py-0.5 rounded flex-none',
                            isJpy
                              ? 'bg-trading-yellow/20 text-trading-yellow'
                              : 'bg-trading-blue/20 text-trading-blue',
                          )}>
                            {isJpy ? 'JPY' : 'USD'}
                          </span>
                        </div>
                      </div>

                      {/* エントリー */}
                      <div className="flex flex-col gap-0.5">
                        <label className="text-[10px] text-trading-muted uppercase tracking-wider">
                          エントリー <span className="normal-case text-trading-muted/60">{isJpy ? '円' : 'USD'}</span>
                        </label>
                        <input
                          type="number" min="0" step="any"
                          className="input-field text-xs"
                          placeholder="0"
                          value={row.entryPrice}
                          onChange={e => updateRow(idx, { entryPrice: e.target.value })}
                        />
                      </div>

                      {/* 利確 */}
                      <div className="flex flex-col gap-0.5">
                        <label className="text-[10px] text-trading-muted uppercase tracking-wider">
                          利確 <span className="normal-case text-trading-muted/60">{isJpy ? '円' : 'USD'}</span>
                        </label>
                        <input
                          type="number" min="0" step="any"
                          className="input-field text-xs"
                          placeholder="0"
                          value={row.takeProfit}
                          onChange={e => updateRow(idx, { takeProfit: e.target.value })}
                        />
                      </div>

                      {/* 損切り */}
                      <div className="flex flex-col gap-0.5">
                        <label className="text-[10px] text-trading-muted uppercase tracking-wider">
                          損切り <span className="normal-case text-trading-muted/60">{isJpy ? '円' : 'USD'}</span>
                        </label>
                        <input
                          type="number" min="0" step="any"
                          className="input-field text-xs"
                          placeholder="0"
                          value={row.stopLoss}
                          onChange={e => updateRow(idx, { stopLoss: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="flex items-end justify-between gap-2">
                      {/* 計算結果 */}
                      <div>
                        {res ? (
                          <>
                            <div className="text-[10px] text-trading-muted">推奨数量</div>
                            <div className="text-sm font-bold text-trading-green tabular-nums">
                              {fmt(res.qty, qtyDecimalsFor(row.symbol))} 単位
                            </div>
                            <div className="text-[10px] text-trading-muted tabular-nums">
                              リスク ¥{fmt(res.riskJpy)}
                            </div>
                            <div className="text-[10px] text-trading-muted tabular-nums">
                              {lev > 1 ? `証拠金 ¥${fmt(res.marginJpy)}` : `投資額 ¥${fmt(res.marginJpy)}`}
                            </div>
                          </>
                        ) : (
                          <div className="text-[10px] text-trading-muted/50 pb-1 leading-tight">
                            エントリー・<br />損切り入力待ち
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1.5 flex-none pb-0.5">
                        {hasSignal && (
                          <button
                            onClick={() => fillRowFromSignal(idx)}
                            title="実シグナルから入力"
                            className="text-trading-blue hover:opacity-70 transition-opacity"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => removeRow(idx)}
                          title="削除"
                          className="text-trading-red hover:opacity-70 transition-opacity"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* 口座レート補正（任意） */}
                    <div className="border-t border-trading-border/50 pt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
                      <div className="flex flex-col gap-0.5">
                        <label className="text-[10px] text-trading-muted uppercase tracking-wider">口座レート</label>
                        <input
                          type="number" min="0" step="any"
                          className="input-field text-xs"
                          placeholder="0"
                          value={row.accountRate}
                          onChange={e => updateRow(idx, { accountRate: e.target.value })}
                        />
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <label className="text-[10px] text-trading-muted uppercase tracking-wider">スプレッド</label>
                        <input
                          type="number" min="0" step="any"
                          className="input-field text-xs"
                          placeholder="0"
                          value={row.spread}
                          onChange={e => updateRow(idx, { spread: e.target.value })}
                        />
                      </div>
                      {orderLevels && (
                        <div className="col-span-2 text-[10px] text-trading-muted leading-tight">
                          <div className="text-[9px] text-trading-muted/70">
                            {orderLevels.dir}シフト {orderLevels.shift >= 0 ? '+' : ''}{orderLevels.shift.toFixed(isJpy ? 3 : 5)}
                          </div>
                          <div className="tabular-nums">
                            発注 E {isJpy ? `¥${fmt(orderLevels.entry, 3)}` : orderLevels.entry.toFixed(5)}
                            {orderLevels.takeProfit != null && (
                              <> / TP <span className="text-trading-green">{isJpy ? `¥${fmt(orderLevels.takeProfit, 3)}` : orderLevels.takeProfit.toFixed(5)}</span></>
                            )}
                            {orderLevels.stopLoss != null && (
                              <> / SL <span className="text-trading-red">{isJpy ? `¥${fmt(orderLevels.stopLoss, 3)}` : orderLevels.stopLoss.toFixed(5)}</span></>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-[11px] text-trading-muted text-center py-3">
              「アラート中のペアを自動取得」または「行を追加」で銘柄を追加してください
            </p>
          )}

          {/* ポートフォリオサマリー */}
          {validCount > 0 && (
            <div className="rounded-md bg-trading-bg border border-trading-border px-3 py-2.5">
              <p className="text-[10px] text-trading-muted uppercase tracking-wider mb-2">ポートフォリオサマリー</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <div className="text-[10px] text-trading-muted">有効ポジション</div>
                  <div className="text-base font-bold text-trading-text tabular-nums">{validCount} ペア</div>
                </div>
                <div>
                  <div className="text-[10px] text-trading-muted">合計リスク額</div>
                  <div className="text-base font-bold text-trading-yellow tabular-nums">¥{fmt(totalRiskJpy)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-trading-muted">残高比リスク</div>
                  <div className={clsx(
                    'text-base font-bold tabular-nums',
                    totalRiskPct > 10 ? 'text-trading-red' : totalRiskPct > 6 ? 'text-trading-yellow' : 'text-trading-green',
                  )}>
                    {totalRiskPct.toFixed(1)}%
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-trading-muted">
                    {lev > 1 ? '合計必要証拠金' : '合計投資額'}
                  </div>
                  <div className={clsx(
                    'text-base font-bold tabular-nums',
                    totalMarginPct > 80 ? 'text-trading-red' : totalMarginPct > 50 ? 'text-trading-yellow' : 'text-trading-text',
                  )}>
                    ¥{fmt(totalMarginJpy)}
                    <span className="text-[10px] text-trading-muted font-normal ml-1">{totalMarginPct.toFixed(1)}%</span>
                  </div>
                </div>
              </div>
              {totalRiskPct > 10 && (
                <p className="mt-2 text-[10px] text-trading-red/80">
                  ⚠️ 合計リスクが残高の10%を超えています。ポジションサイズの見直しを検討してください。
                </p>
              )}
            </div>
          )}

          <p className="text-[9px] text-trading-muted/50 leading-relaxed">
            ※ 各ペアに同一リスク割合を適用する固定フラクショナル分散法です。USD建てペアはUSD/JPYレートで円換算しています。証拠金はレバレッジ入力時のみ正確に計算されます。
          </p>
        </div>
      )}
    </div>
  )
}
